import { useCallback, useState } from 'react';

import { checkFiscalCode } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useField, validateAll } from '../../hooks/field';
import { useUpdateProfile } from '../../hooks/update-profile';
import { useVisit } from '../../hooks/visit';

import { PageTitle } from '../page-title';
import { StepActions } from '../step-actions';
import { TextField } from '../text-field';

import styles from './style.module.css';

// Step 4, the only one that can be skipped (C19). Finishing with an empty
// field and skipping both close onboarding without a code. The check is the
// format only, and nothing here verifies whose code it is.
export function FiscalCodeStep({ profile, onBack, onDone }) {
  const { text, translate } = useLanguage();
  const updateProfile = useUpdateProfile();
  const capture = useVisit();
  const fiscalCode = useField({ initial: profile.fiscalCode, check: checkFiscalCode });
  const [ busy, setBusy ] = useState(false);

  const save = useCallback(function(input) {
    setBusy(true);

    const owns = capture();

    return updateProfile(input).then(function() {
      if (owns() === false) {
        return null;
      }

      return onDone();
    }).catch(function(failed) {
      if (owns() === false) {
        return;
      }

      setBusy(false);
      fiscalCode.refuse(failed.message);
    });
  }, [ fiscalCode, updateProfile, onDone, capture ]);

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ fiscalCode ]) === false) {
      return null;
    }

    return save({ fiscalCode: fiscalCode.value, finish: true });
  }, [ fiscalCode, save ]);

  // Skipping sends no code at all, so whatever is in the field is not checked.
  const onSkip = useCallback(function() {
    return save({ finish: true });
  }, [ save ]);

  return (
    <form className={ styles.form } onSubmit={ onSubmit } noValidate>
      <PageTitle title={ text.fiscalTitle } intro={ text.fiscalIntro }/>

      <TextField
        ref={ fiscalCode.ref }
        id='fiscal-code'
        label={ text.fiscalCode }
        value={ fiscalCode.value }
        onChange={ fiscalCode.onChange }
        onBlur={ fiscalCode.onBlur }
        error={ translate(fiscalCode.error) }
        placeholder={ text.fiscalPlaceholder }
      />

      <StepActions onBack={ onBack } submitLabel={ text.finish } busy={ busy } skipLabel={ text.skipForNow } onSkip={ onSkip }/>
    </form>
  );
}
